import React, { useState, useEffect, useRef } from 'react';
import { usePhrase } from 'hooks/general';
import { Icon } from 'components/general/Icon/Icon';
import { TextField, TextFieldProps } from '@/components/forms/inputs/TextField/TextField';
import {
    Control,
    useWatch,
    Path,
    PathValue,
    FieldValues,
    UseFormSetValue,
    FieldError,
} from 'react-hook-form';
import { Button, Grid, IconButton, Skeleton, Typography } from '@mui/material';
import { Phrases } from 'consts/phrases';
import { InputProps } from '@mui/material/Input';
import { applyMaskToString, MaskDefinition } from '@/utils/mask/mask';

interface EditableTextFieldProps<T extends FieldValues> extends TextFieldProps<T> {
    initialValue?: string;
    onValueChange?: (value: string | null) => void;
    onIsEditingChange?: (isEditing: boolean) => void;
    placeholder?: string;
    label?: string | number;
    required?: boolean;
    control: Control<T>;
    name: Path<T>;
    slotProps?: {
        input?: Partial<InputProps>;
    };
    setValue?: UseFormSetValue<T>;
    error?: FieldError | null;
    textMask?: string;
    textMaskDefinition?: MaskDefinition;
    isDataLoading?: boolean;
}

export const EditableTextField = <T extends FieldValues>({
    initialValue,
    onValueChange,
    onIsEditingChange,
    placeholder,
    error,
    label,
    required,
    control,
    name,
    slotProps,
    setValue,
    textMask,
    textMaskDefinition,
    isDataLoading = false,
    ...props
}: EditableTextFieldProps<T>) => {
    const { t } = usePhrase();
    const [isEditing, setIsEditing] = useState(false);
    const originalValue = useRef(initialValue || '');
    const editInitialValue = useRef<string | null>(null);

    const currentValue = useWatch({
        control,
        name,
        defaultValue: initialValue as PathValue<T, Path<T>>,
    });

    const handleIsEditingChange = (isEditing: boolean) => {
        setIsEditing(isEditing);
        onIsEditingChange?.(isEditing);
    };

    useEffect(() => {
        if (initialValue) {
            originalValue.current = initialValue;
            setIsEditing(false);
        }
    }, [initialValue, name]);

    const handleEditClick = () => {
        editInitialValue.current = originalValue.current;
        if (setValue) {
            setValue(name, '' as PathValue<T, Path<T>>);
        }
        handleIsEditingChange(true);
    };

    const handleCancelClick = () => {
        if (onValueChange) {
            onValueChange(null);
        }
        if (setValue) {
            setValue(name, editInitialValue.current as PathValue<T, Path<T>>);
        }
        handleIsEditingChange(false);
    };

    const handleBlur = () => {
        if (isEditing && onValueChange && currentValue !== originalValue.current) {
            onValueChange(String(currentValue || ''));
            handleIsEditingChange(false);
        }
    };

    if (isDataLoading) {
        return <Skeleton key={props.key} height={45} width="100%" />;
    }

    if (!initialValue) {
        return (
            <TextField<T>
                label={label as string}
                fullWidth
                error={error}
                control={control}
                name={name}
                placeholder={placeholder}
                required={required}
                slotProps={slotProps}
                {...props}
            />
        );
    }

    return (
        <>
            {isEditing ? (
                <TextField<T>
                    label={label as string}
                    fullWidth
                    control={control}
                    name={name}
                    error={error}
                    placeholder={placeholder}
                    required={required}
                    autoFocus
                    onBlur={handleBlur}
                    slotProps={{
                        input: {
                            endAdornment: (
                                <Button size="small" onClick={handleCancelClick}>
                                    {t(Phrases.CANCEL)}
                                </Button>
                            ),
                        },
                        inputLabel: {
                            shrink: true,
                        },
                    }}
                    sx={{
                        '.MuiInputBase-adornedEnd': {
                            paddingRight: 0,
                        },
                    }}
                    {...props}
                />
            ) : (
                <Grid container size={12} sx={{ mt: -3 }} direction={'column'}>
                    <Grid>
                        {label && (
                            <Typography
                                variant="caption"
                                sx={(theme) => ({
                                    color: theme.colors.other.text.darkGray,
                                })}
                            >
                                {required && (
                                    <span style={{ color: 'red', marginRight: '4px' }}>*</span>
                                )}
                                {label}
                            </Typography>
                        )}
                    </Grid>
                    <Grid container size={12} sx={{ alignItems: 'center', mt: 0.5 }}>
                        <Typography>
                            {applyMaskToString({
                                value: String(initialValue),
                                mask: textMask ?? props.mask,
                                maskDefinition: textMaskDefinition ?? props.maskDefinition,
                            })}
                        </Typography>
                        <IconButton
                            data-testid="edit-button"
                            sx={(theme) => ({
                                ml: '10px',
                                color: theme.colors.other.text.darkGray,
                            })}
                            onClick={handleEditClick}
                        >
                            <Icon path="general/EditIcon" />
                        </IconButton>
                    </Grid>
                </Grid>
            )}
        </>
    );
};
